import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, ArrowRight, Filter } from 'lucide-react';
import { getNews } from '../services/cmsData';

export default function NewsPage() {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    const fetchNews = async () => {
      setLoading(true);
      try {
        const data = await getNews({ category: selectedCategory === 'all' ? null : selectedCategory });
        setNews(data);
      } catch (error) {
        console.error('Error fetching news:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchNews();
  }, [selectedCategory]);

  const categories = [
    { value: 'all', label: 'All News' },
    { value: 'announcement', label: 'Announcements' },
    { value: 'press', label: 'Press' },
    { value: 'company', label: 'Company' },
    { value: 'partnership', label: 'Partnerships' },
    { value: 'behind-scenes', label: 'Behind the Scenes' },
    { value: 'milestone', label: 'Milestones' },
  ];

  return (
    <div data-testid="news-page" className="min-h-screen">
      {/* Hero */}
      <section className="relative pt-32 pb-20 px-6 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-4xl mx-auto"
          >
            <p className="font-accent text-brand-primary text-sm tracking-[0.3em] mb-6 uppercase">
              Latest Updates
            </p>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold text-brand-dark mb-8 leading-tight">
              News & <span className="text-brand-primary">Press</span>
            </h1>
            <p className="font-body text-gray-600 text-xl">
              Stay up to date with announcements, press releases, and stories from behind the curtain
            </p>
          </motion.div>
        </div>
      </section>

      {/* Category Filters */}
      <section className="py-8 bg-white sticky top-16 z-30 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((cat) => (
              <button
                key={cat.value}
                onClick={() => setSelectedCategory(cat.value)}
                data-testid={`news-filter-${cat.value}`}
                className={`px-6 py-3 rounded-full font-body text-sm font-medium transition-all duration-300 ${
                  selectedCategory === cat.value
                    ? 'bg-brand-primary text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* News Grid */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="bg-gray-100 rounded-3xl h-96 animate-pulse" />
              ))}
            </div>
          ) : news.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10">
              {news.map((article, index) => (
                <motion.article
                  key={article.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  data-testid={`news-article-${article.id}`}
                  className="group"
                >
                  <Link to={`/news/${article.id}`} className="block h-full">
                    <div className="bg-gray-50 rounded-3xl overflow-hidden h-full flex flex-col hover:shadow-2xl hover:-translate-y-2 transition-all duration-500">
                      <div className="relative h-56 overflow-hidden">
                        <img
                          src={article.image.startsWith('http') ? article.image : `${process.env.PUBLIC_URL}${article.image}`}
                          alt={article.title}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                        />
                        <div className="absolute top-4 left-4 right-4 flex justify-between items-start">
                          <span className="px-3 py-1.5 rounded-full bg-accent-gold/90 backdrop-blur-sm text-brand-dark text-[11px] font-semibold capitalize tracking-wide">
                            {article.category.replace(/-/g, ' ')}
                          </span>
                          {article.featured && (
                            <span className="px-3 py-1.5 rounded-full bg-white/90 backdrop-blur-sm text-brand-dark text-[11px] font-bold">
                              Featured
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <div className="p-8 flex-1 flex flex-col">
                        <time className="font-body text-sm text-brand-primary font-medium flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          {new Date(article.date).toLocaleDateString('en-GB', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric'
                          })}
                        </time>
                        
                        <h2 className="font-display text-xl font-bold text-brand-dark mt-3 group-hover:text-brand-primary transition-colors">
                          {article.title}
                        </h2>
                        
                        <p className="font-body text-gray-600 mt-4 flex-1 line-clamp-3">
                          {article.excerpt}
                        </p>
                        
                        <div className="mt-6 pt-6 border-t border-gray-200">
                          <span className="inline-flex items-center gap-2 text-brand-primary font-semibold group-hover:gap-4 transition-all">
                            Read More
                            <ArrowRight className="w-4 h-4" />
                          </span>
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.article>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-24 h-24 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-6">
                <Filter className="w-12 h-12 text-gray-400" />
              </div>
              <h3 className="font-display text-2xl font-bold text-brand-dark mb-3">
                No Articles Found
              </h3>
              <p className="font-body text-gray-600 mb-8">
                No news articles in this category yet. Check back soon!
              </p>
              <button
                onClick={() => setSelectedCategory('all')}
                className="px-8 py-4 bg-brand-primary text-white rounded-full font-semibold hover:bg-brand-dark transition-colors"
              >
                View All News
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-20 bg-brand-primary">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-white mb-6">
            Stay in the Loop
          </h2>
          <p className="font-body text-white/80 text-lg mb-8">
            Be the first to hear about new shows, exclusive presales, and behind-the-scenes content.
          </p>
          <Link
            to="/contact"
            data-testid="newsletter-signup-btn"
            className="inline-flex items-center gap-3 bg-accent-gold text-brand-dark px-10 py-5 rounded-full font-bold text-lg transition-all duration-300 hover:bg-white hover:scale-105"
          >
            Get Updates
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
}
